import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import type {
  MeetingScope,
  RecallPreference,
  UpdatablePreferences,
} from '@/hooks/integrations/recall/useRecallPreference';
import { BadgeWithButton } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { NativeSelect } from '@/lib/base/forms/select/select-native';
import { logger } from '@/lib/logger';

import { BotAvatarUpload } from './bot-avatar-upload';

// ---------------------------------------------------------------------------
// Module-scope constants
// ---------------------------------------------------------------------------

const SCOPE_OPTIONS = [
  { label: 'All meetings', value: 'all' as const },
  {
    label: 'External only',
    value: 'external_only' as const,
  },
];

const BOT_NAME_PLACEHOLDER = 'Ahoy Notetaker';
const BOT_NAME_MAX_LENGTH = 200;
const EXCLUSIONS_MAX_ENTRIES = 100;

/** Trim + lowercase — pure function, no need for useCallback. */
function normalizeExclusion(raw: string): string {
  return raw.trim().toLowerCase();
}

const INPUT_CLASS =
  'w-full px-3 py-2 text-sm border border-border-secondary rounded-lg bg-bg-primary text-primary focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-v2-primary)] disabled:opacity-50 disabled:cursor-not-allowed';

// ---------------------------------------------------------------------------
// ExclusionChip — sub-component so onRemove is stable
// via useCallback
// ---------------------------------------------------------------------------

interface ExclusionChipProps {
  entry: string;
  onRemove: (entry: string) => void;
}

function ExclusionChip({ entry, onRemove }: ExclusionChipProps) {
  const handleClick = useCallback(() => onRemove(entry), [onRemove, entry]);
  return (
    <BadgeWithButton
      type='pill-color'
      size='sm'
      color='gray'
      buttonLabel={`Remove ${entry}`}
      onButtonClick={handleClick}
    >
      {entry}
    </BadgeWithButton>
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface PersonalAdvancedSettingsProps {
  preference: RecallPreference;
  setPreference: (value: UpdatablePreferences) => Promise<void>;
}

// ---------------------------------------------------------------------------
// PersonalAdvancedSettings — advanced fields shown when
// recording is ON.
//
// Layout: three logical groups —
//   1. Meetings to record (scope selector)
//   2. Bot appearance (avatar + name, side by side)
//   3. Exclusions (tag input)
// ---------------------------------------------------------------------------

export function PersonalAdvancedSettings({
  preference,
  setPreference,
}: PersonalAdvancedSettingsProps) {
  // All hooks before any conditional return (CLAUDE.md Rule 5)

  // --- Meeting scope ---
  const currentScope: MeetingScope = preference.meeting_scope ?? 'all';

  const handleScopeChange = useCallback(
    async (e: React.ChangeEvent<HTMLSelectElement>) => {
      const next = e.target.value as MeetingScope;
      try {
        await setPreference({ meeting_scope: next });
      } catch {
        toast.error("We couldn't save the meeting scope. Try again.");
      }
    },
    [setPreference]
  );

  // --- Bot name ---
  const [botNameDraft, setBotNameDraft] = useState(preference.bot_name);

  const handleBotNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setBotNameDraft(e.target.value.slice(0, BOT_NAME_MAX_LENGTH));
    },
    []
  );

  const handleBotNameBlur = useCallback(async () => {
    const trimmed = botNameDraft.trim();
    if (trimmed === preference.bot_name) {
      return;
    }
    try {
      await setPreference({ bot_name: trimmed });
      setBotNameDraft(trimmed);
    } catch {
      setBotNameDraft(preference.bot_name);
      toast.error("We couldn't save the bot name. Try again.");
    }
  }, [botNameDraft, preference.bot_name, setPreference]);

  // --- Bot avatar ---
  const [avatarUrl, setAvatarUrl] = useState(preference.bot_avatar_url);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [avatarUrlDraft, setAvatarUrlDraft] = useState(
    preference.bot_avatar_url
  );

  const handleAvatarUploaded = useCallback(
    async (url: string) => {
      setAvatarUrl(url);
      setAvatarUrlDraft(url);
      try {
        await setPreference({ bot_avatar_url: url });
      } catch {
        setAvatarUrl(preference.bot_avatar_url);
        setAvatarUrlDraft(preference.bot_avatar_url);
        toast.error("We couldn't save the bot avatar. Try again.");
      }
    },
    [setPreference, preference.bot_avatar_url]
  );

  const handleAvatarRemoved = useCallback(async () => {
    setAvatarUrl('');
    setAvatarUrlDraft('');
    try {
      await setPreference({ bot_avatar_url: '' });
    } catch {
      setAvatarUrl(preference.bot_avatar_url);
      setAvatarUrlDraft(preference.bot_avatar_url);
      toast.error("We couldn't remove the bot avatar. Try again.");
    }
  }, [setPreference, preference.bot_avatar_url]);

  const handleToggleUrlInput = useCallback(() => {
    setShowUrlInput(prev => !prev);
  }, []);

  const handleAvatarUrlDraftChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setAvatarUrlDraft(e.target.value);
    },
    []
  );

  const handleAvatarUrlDraftBlur = useCallback(async () => {
    const trimmed = avatarUrlDraft.trim();
    if (trimmed === preference.bot_avatar_url) {
      return;
    }
    try {
      await setPreference({ bot_avatar_url: trimmed });
      setAvatarUrl(trimmed);
      setAvatarUrlDraft(trimmed);
    } catch {
      setAvatarUrlDraft(preference.bot_avatar_url);
      setAvatarUrl(preference.bot_avatar_url);
      toast.error("We couldn't save the bot avatar URL. Try again.");
    }
  }, [avatarUrlDraft, preference.bot_avatar_url, setPreference]);

  // --- Exclusions ---
  const [exclusionsDraft, setExclusionsDraft] = useState<string[]>(
    preference.exclusions
  );
  const [exclusionInput, setExclusionInput] = useState('');
  const [exclusionError, setExclusionError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleExclusionInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setExclusionInput(e.target.value);
    },
    []
  );

  const persistExclusions = useCallback(
    async (next: string[], previous: string[]) => {
      setSaving(true);
      setExclusionError(null);
      setExclusionsDraft(next);
      try {
        await setPreference({ exclusions: next });
      } catch (err) {
        setExclusionsDraft(previous);
        logger.error(
          'Failed to save recording exclusions',
          err instanceof Error ? err : new Error(String(err)),
          { component: 'PersonalAdvancedSettings' }
        );
        setExclusionError("We couldn't save that. Try again.");
      } finally {
        setSaving(false);
      }
    },
    [setPreference]
  );

  const handleAddExclusion = useCallback(() => {
    if (saving) {
      return;
    }
    const normalized = normalizeExclusion(exclusionInput);
    if (!normalized) {
      setExclusionError('Enter an email or domain');
      return;
    }
    if (exclusionsDraft.length >= EXCLUSIONS_MAX_ENTRIES) {
      setExclusionError(`Maximum ${EXCLUSIONS_MAX_ENTRIES} entries`);
      return;
    }
    if (exclusionsDraft.includes(normalized)) {
      setExclusionInput('');
      setExclusionError(null);
      return;
    }
    setExclusionInput('');
    setExclusionError(null);
    persistExclusions([...exclusionsDraft, normalized], exclusionsDraft);
  }, [saving, exclusionInput, exclusionsDraft, persistExclusions]);

  const handleRemoveExclusion = useCallback(
    (entry: string) => {
      if (saving) {
        return;
      }
      persistExclusions(
        exclusionsDraft.filter(e => e !== entry),
        exclusionsDraft
      );
    },
    [saving, exclusionsDraft, persistExclusions]
  );

  const handleExclusionKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAddExclusion();
      }
    },
    [handleAddExclusion]
  );

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <div className='space-y-6'>
      {/* ── Meetings to record ── */}
      <div>
        <label
          htmlFor='notetaker-scope'
          className='block text-sm font-medium text-fg-primary mb-1'
        >
          Meetings to record
        </label>
        <p className='text-xs text-fg-tertiary mb-2'>
          Choose which calendar events the bot should join.
        </p>
        <NativeSelect
          id='notetaker-scope'
          value={currentScope}
          onChange={handleScopeChange}
          options={SCOPE_OPTIONS}
          size='sm'
        />
      </div>

      {/* ── Divider ── */}
      <div className='border-t border-border-secondary' />

      {/* ── Bot appearance ── */}
      <fieldset className='space-y-3'>
        <legend className='text-sm font-medium text-fg-primary'>
          Bot appearance
        </legend>
        <p className='text-xs text-fg-tertiary -mt-1'>
          Customise how the bot looks when it joins meetings.
        </p>

        {/* Card wrapping avatar + name + URL fallback */}
        <div className='rounded-lg border border-border-secondary p-5 space-y-4'>
          <div className='flex items-start gap-5'>
            {/* Avatar column — centered vertically */}
            <BotAvatarUpload
              currentUrl={avatarUrl}
              onUploaded={handleAvatarUploaded}
              onRemoved={handleAvatarRemoved}
            />

            {/* Name column */}
            <div className='flex-1 min-w-0 pt-1'>
              <label
                htmlFor='notetaker-bot-name'
                className='block text-xs font-medium text-fg-secondary mb-1'
              >
                Bot name
              </label>
              <input
                id='notetaker-bot-name'
                type='text'
                value={botNameDraft}
                onChange={handleBotNameChange}
                onBlur={handleBotNameBlur}
                placeholder={BOT_NAME_PLACEHOLDER}
                maxLength={BOT_NAME_MAX_LENGTH}
                className={INPUT_CLASS}
              />
              <p className='mt-1 text-xs text-fg-tertiary'>
                Shown to other participants when the bot joins.
              </p>
            </div>
          </div>

          {/* URL fallback — inside the card, subtle separator */}
          <div className='border-t border-border-secondary pt-3'>
            <button
              type='button'
              onClick={handleToggleUrlInput}
              className='text-xs text-fg-tertiary hover:text-fg-secondary underline'
            >
              {showUrlInput ? 'Hide URL input' : 'Or paste an image URL'}
            </button>
            {showUrlInput && (
              <div className='mt-2'>
                <input
                  id='notetaker-bot-avatar-url'
                  type='url'
                  value={avatarUrlDraft}
                  onChange={handleAvatarUrlDraftChange}
                  onBlur={handleAvatarUrlDraftBlur}
                  placeholder='https://example.com/avatar.png'
                  className={INPUT_CLASS}
                  aria-label='Bot avatar URL'
                />
              </div>
            )}
          </div>
        </div>
      </fieldset>

      {/* ── Divider ── */}
      <div className='border-t border-border-secondary' />

      {/* ── Exclusions ── */}
      <section aria-labelledby='notetaker-exclusions-heading'>
        <p
          id='notetaker-exclusions-heading'
          className='text-sm font-medium text-fg-primary mb-1'
        >
          Excluded emails and domains
        </p>
        <p className='text-xs text-fg-tertiary mb-2'>
          Skip recording meetings where these addresses or domains are present.
          Enter an email (e.g. boss@example.com) or domain (e.g. example.com).
        </p>
        <ul role='list' className='flex flex-wrap gap-2 mb-2'>
          {exclusionsDraft.length === 0 ? (
            <li className='text-xs text-fg-tertiary'>No exclusions.</li>
          ) : (
            exclusionsDraft.map(entry => (
              <li key={entry}>
                <ExclusionChip entry={entry} onRemove={handleRemoveExclusion} />
              </li>
            ))
          )}
        </ul>
        <div className='space-y-1'>
          <label
            htmlFor='notetaker-exclusion-input'
            className='text-xs font-medium text-fg-secondary block'
          >
            Add email or domain
          </label>
          <div className='flex gap-2'>
            <input
              id='notetaker-exclusion-input'
              type='text'
              value={exclusionInput}
              onChange={handleExclusionInputChange}
              onKeyDown={handleExclusionKeyDown}
              aria-invalid={exclusionError !== null}
              aria-describedby={
                exclusionError ? 'notetaker-exclusion-error' : undefined
              }
              placeholder='user@example.com or example.com'
              className={INPUT_CLASS}
              disabled={
                saving || exclusionsDraft.length >= EXCLUSIONS_MAX_ENTRIES
              }
            />
            <Button
              color='secondary'
              size='sm'
              onClick={handleAddExclusion}
              isDisabled={
                saving || exclusionsDraft.length >= EXCLUSIONS_MAX_ENTRIES
              }
            >
              Add
            </Button>
          </div>
          {exclusionError && (
            <p
              id='notetaker-exclusion-error'
              role='alert'
              className='text-xs text-utility-error-600'
            >
              {exclusionError}
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
